import {IAuth} from '@src/@interfaces/services/Auth/IAuth';
import {IUser} from '@src/@interfaces/services/Auth/IUser';
import app from '@src/helpers/app';
import Storage from '@src/services/Storage/Storage';
import {ObjectKeys} from '@src/@types/ObjectKeys';
import User from './User';
import UnauthorizedHttpException from '@src/exceptions/UnauthorizedHttpException';
import {IStorage} from '@src/@interfaces/services/Storage/IStorage';
import {clearUserRequisites} from '@src/_redux/user/actions';
import {IListener} from '@src/@interfaces/services/Events';
import {ISubscription} from '@src/@interfaces/services/Events/ISubscription';

enum Events {
    DETAILS_CHANGED = 'auth@details-changed',
}

export default class Auth implements IAuth {
    protected token: string | null = null;
    protected user: IUser | null = null;

    protected storeInstance?: IStorage;

    constructor() {
        this.token = this.store.get('access-token');
    }

    isAuthorized(): boolean {
        return this.getUser() !== null;
    }

    getAccessToken(): string | null {
        return this.token;
    }

    getUserId(): string | number | null {
        return this.getUser()?.id ?? null;
    }

    getUser(): IUser | null {
        return this.user;
    }

    getNullUser(): IUser {
        return new User({
            id: 0,
            email: '',
            is_super_user: false,
            is_online: false,
        });
    }

    async fetchUser(): Promise<IUser | null> {
        return app('http').route('profile.me').then((userAttributes: ObjectKeys<IUser>) => {
            return this.resolveUserModelByAttributes(userAttributes);
        }).catch(error => {
            if (error instanceof UnauthorizedHttpException) {
                this.clearRequisites();// prevent request to auth.logout route by clearing access token
                app().dispatchAction(clearUserRequisites());

                return null;
            }

            throw error;
        });
    }

    async login(requestBody: Record<string, string>): Promise<any> {
        return app('http').route('auth.login', undefined, {
            body: JSON.stringify(requestBody),
        });
    }

    async logout(): Promise<any> {
        if (!this.getAccessToken()) {
            this.clearRequisites();
        }

        return app('http').route('auth.logout').finally(() => {
            this.clearRequisites();
        });
    }

    setAccessToken(accessToken: string): void {
        this.store.set('access-token', accessToken);
        this.token = accessToken;
    }

    setUser(user: IUser): void {
        this.user = user;

        app('events').dispatch(Events.DETAILS_CHANGED, {user});
    }

    setUserByAttributes(userAttributes: ObjectKeys<IUser>): void {
        this.setUser(
            this.resolveUserModelByAttributes(userAttributes)
        );
    }

    onUserDetailsChanged(listener: IListener<{ user: IUser | null }>): ISubscription {
        return app('events').listen(Events.DETAILS_CHANGED, listener);
    }

    protected get store() {
        return this.storeInstance ??= new Storage('auth@');
    }

    protected resolveUserModelByAttributes(userAttributes: ObjectKeys<IUser>): IUser {
        return new User(userAttributes);
    }

    protected clearRequisites(): void {
        this.token = this.user = null;
        this.store.remove('access-token');

        app('events').dispatch(Events.DETAILS_CHANGED, {user: null});
    }
}
